import type { Comissao, ContaComSaldo } from '@pgcustom/shared-types';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { ComissoesApi } from './comissoes.api.js';

/**
 * `<erp-pagar-comissao-modal>` — pagamento de comissão (Sprint 7,
 * `PRODUCT.md`, 3.6: "o pagamento de comissão gera uma transação de
 * despesa no financeiro"). Exige escolher a conta de origem do
 * pagamento explicitamente — não existe conta padrão de comissão
 * documentada. Mesmo padrão de `erp-transacao-form-modal`.
 */
export class ErpPagarComissaoModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _comissoesApi: ComissoesApi | null = null;
  private _aoPagar: ((comissao: Comissao) => void) | null = null;

  private aberto = false;
  private comissaoAtual: Comissao | null = null;
  private contas: ContaComSaldo[] = [];
  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set comissoesApi(valor: ComissoesApi) {
    this._comissoesApi = valor;
  }

  set aoPagar(valor: (comissao: Comissao) => void) {
    this._aoPagar = valor;
  }

  abrir(comissao: Comissao, contas: ContaComSaldo[]): void {
    this.comissaoAtual = comissao;
    this.contas = contas;
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  connectedCallback(): void {
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private async aoSubmeter(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._comissoesApi || !this.comissaoAtual || this.enviando) return;

    const contaId = this.root.querySelector<HTMLSelectElement>('#conta')?.value ?? '';
    if (!contaId) {
      this.mensagemErro = 'selecione uma conta';
      this.render();
      return;
    }

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      const comissao = await this._comissoesApi.pagar(this.comissaoAtual.id, contaId);
      this.aberto = false;
      this._aoPagar?.(comissao);
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível pagar. tente novamente.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private render(): void {
    const comissao = this.comissaoAtual;

    this.root.innerHTML = `
      <style>
        :host { display: block; }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); }
        p { margin: 0; font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); }
        ${FORM_FIELD_CSS}
        .rodape { display: flex; justify-content: flex-end; gap: var(--pg-space-3, 12px); }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">Pagar comissão</span>
        <form novalidate>
          <p>Valor: ${comissao ? formatarCentavos(comissao.valorCentavos) : ''} — de onde sai o pagamento?</p>
          <label>
            Conta
            <select id="conta">
              ${this.contas.map((c) => `<option value="${c.id}">${escaparHtml(c.nome)}</option>`).join('')}
            </select>
          </label>
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
        </form>
        <div slot="footer" class="rodape">
          <pg-button id="botao-cancelar" variant="ghost">Cancelar</pg-button>
          <pg-button id="botao-pagar" variant="primary" ${this.enviando ? 'loading' : ''}>Pagar</pg-button>
        </div>
      </pg-modal>
    `;

    const form = this.root.querySelector('form');
    form?.addEventListener('submit', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-pagar')?.addEventListener('pg-click', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-cancelar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());
  }
}

function formatarCentavos(centavos: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(centavos / 100);
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-pagar-comissao-modal', ErpPagarComissaoModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-pagar-comissao-modal': ErpPagarComissaoModal;
  }
}
